import React, { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import { Container, Grid, Card, Divider, List, ListItem, ListItemText, Link } from "@mui/material";

const PageShell = ({ title, subtitle, children }) => (
  <MKBox component="section" sx={{ backgroundColor: "#0b1220" }}>
    {/* Hero */}
    <MKBox
      sx={{
        background: "linear-gradient(135deg, #001f3f 0%, #003060 50%, #0b1220 100%)",
        color: "#fff",
        py: { xs: 6, md: 10 },
      }}
    >
      <Container maxWidth="lg">
        <MKTypography
          variant="h2"
          color="white"
          fontWeight="bold"
          mb={1}
          sx={{ textTransform: "uppercase", letterSpacing: 1.5 }}
        >
          {title}
        </MKTypography>
        <MKTypography variant="body1" color="white" sx={{ opacity: 0.9 }}>
          {subtitle}
        </MKTypography>
      </Container>
    </MKBox>

    {/* Content */}
    <Container maxWidth="lg">
      <Grid container spacing={3} sx={{ my: { xs: 2, md: 4 } }}>
        {children}
      </Grid>
    </Container>
  </MKBox>
);

const BackToTop = () => (
  <MKBox textAlign="right" my={4}>
    <MKButton component="a" href="#top" variant="gradient" color="info">
      Back to top
    </MKButton>
  </MKBox>
);

export default function TermsAndConditions() {
  const company = "The Best Helper"; // change to your brand
  const lastUpdated = "August 30, 2025";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: "intro", label: "Introduction" },
    { id: "eligibility", label: "Eligibility & Account" },
    { id: "services", label: "Scope of Services" },
    { id: "user_duties", label: "User Duties" },
    { id: "booking", label: "Requests, Bookings & Offers" },
    { id: "payments", label: "Pricing, Fees & Payments" },
    { id: "verification", label: "Verification & Due Diligence" },
    { id: "thirdparties", label: "Third‑Party Providers" },
    { id: "property_vehicle_land", label: "Property, Vehicles & Land" },
    { id: "employment", label: "Household Help & Jobs" },
    { id: "content", label: "User Content & Reviews" },
    { id: "prohibited", label: "Prohibited Uses" },
    { id: "ip", label: "Intellectual Property" },
    { id: "privacy", label: "Privacy & Communications" },
    { id: "warranties", label: "Disclaimers & Warranties" },
    { id: "liability", label: "Limitation of Liability" },
    { id: "indemnity", label: "Indemnification" },
    { id: "termination", label: "Suspension & Termination" },
    { id: "law", label: "Governing Law & Disputes" },
    { id: "changes", label: "Changes to Terms" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <MKBox id="top">
      <PageShell
        title="Terms & Conditions"
        subtitle={`${company} connects newcomers and diaspora to trusted housing, vehicles with drivers, land purchases, household help, and job matching across East Africa.`}
      >
        {/* TOC */}
        <Grid item xs={12} md={4} lg={3}>
          <Card sx={{ position: { md: "sticky" }, top: { md: 32 }, p: 2, borderRadius: 3 }}>
            <MKTypography variant="h6" fontWeight="bold" mb={1}>
              Table of Contents
            </MKTypography>
            <Divider sx={{ mb: 1 }} />
            <List dense>
              {sections.map((s) => (
                <ListItem key={s.id} sx={{ py: 0 }}>
                  <Link href={`#${s.id}`} underline="hover">
                    <ListItemText primary={s.label} />
                  </Link>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 1 }} />
            <MKTypography variant="caption" color="text">
              Last updated: {lastUpdated}
            </MKTypography>
          </Card>
        </Grid>

        {/* Main body */}
        <Grid item xs={12} md={8} lg={9}>
          <Card sx={{ p: { xs: 2, md: 4 }, borderRadius: 4 }}>
            <Section id="intro" title="1. Introduction">
              <p>
                Welcome to {company}. By accessing or using our website, mobile interfaces, or
                contacting our team (collectively, the “Platform”), you agree to these Terms &
                Conditions (“Terms”). If you do not agree, please do not use the Platform. These
                Terms form a binding agreement between you and {company}, a service facilitator
                operating within the East African Community (including Rwanda, Kenya, Uganda, and
                Tanzania).
              </p>
              <p>
                We provide discovery, coordination, and concierge-style matching for: (a) houses to
                rent or buy; (b) cars to rent or buy, with optional professional drivers; (c) land
                and plots for purchase; (d) hiring household help; and (e) basic job-matching
                between employers and candidates. We are not the owner, seller, broker of record, or
                employer of record for the underlying services unless explicitly stated in a written
                agreement.
              </p>
            </Section>

            <Section id="eligibility" title="2. Eligibility & Account">
              <ul>
                <li>You must be at least 18 years old and legally able to enter into contracts.</li>
                <li>
                  When creating an account or submitting a request, you agree to provide true,
                  current, and complete information.
                </li>
                <li>
                  You are responsible for safeguarding your credentials and for all activity under
                  your account.
                </li>
              </ul>
            </Section>

            <Section id="services" title="3. Scope of Services">
              <p>
                {company} acts as an intermediary and coordination layer. We curate options,
                introduce you to third‑party providers, and streamline bookings or purchases. Any
                statements regarding availability, pricing, or suitability are based on information
                provided by third parties and may change without notice.
              </p>
            </Section>

            <Section id="user_duties" title="4. User Duties">
              <ul>
                <li>Use the Platform lawfully and respectfully.</li>
                <li>Provide accurate preferences (budget, timing, location, requirements).</li>
                <li>Attend viewings/meetings on time; communicate changes promptly.</li>
                <li>
                  Comply with applicable immigration, residency, tenancy, property, transport, and
                  labor rules.
                </li>
              </ul>
            </Section>

            <Section id="booking" title="5. Requests, Bookings & Offers">
              <p>
                Submitting a request authorizes us to contact suitable providers on your behalf.
                Quotations, offers, or itineraries are not guaranteed until confirmed in writing by
                the provider and, where applicable, a deposit is received. Some offers are
                time‑limited and subject to inventory changes.
              </p>
            </Section>

            <Section id="payments" title="6. Pricing, Fees & Payments">
              <ul>
                <li>
                  We may charge service, coordination, or success fees disclosed before you commit.
                </li>
                <li>
                  Third‑party providers set their own prices, deposits, and cancellation terms.
                </li>
                <li>
                  Where we process payments, you authorize us to charge the selected method for
                  agreed amounts, taxes, and fees.
                </li>
                <li>
                  Refunds, if any, follow the applicable provider’s policy, unless otherwise
                  specified in writing.
                </li>
              </ul>
            </Section>

            <Section id="verification" title="7. Verification & Due Diligence">
              <p>
                We may perform limited verification (e.g., identity or documentation checks) to
                reduce fraud. However, you should perform your own due diligence, especially for
                property and land transactions, including title searches, valuation, inspection, and
                legal review.
              </p>
            </Section>

            <Section id="thirdparties" title="8. Third‑Party Providers">
              <p>
                Services are delivered by independent providers (landlords, agents, drivers, car
                rental firms, land sellers, and domestic staff). We do not control their operations
                and do not warrant their performance, licensing, insurance, or compliance. Your
                contract is typically with the provider.
              </p>
            </Section>

            <Section id="property_vehicle_land" title="9. Property, Vehicles & Land">
              <ul>
                <li>
                  <strong>Homes:</strong> Verify condition, utilities, neighborhood, lease terms,
                  and any association or government rules.
                </li>
                <li>
                  <strong>Vehicles:</strong> Confirm roadworthiness, insurance, driver licensing,
                  mileage limits, and security deposits.
                </li>
                <li>
                  <strong>Land:</strong> Conduct title and boundary checks, zoning, permitted use,
                  and environmental constraints with qualified professionals.
                </li>
              </ul>
            </Section>

            <Section id="employment" title="10. Household Help & Jobs">
              <p>
                When we assist with hiring (e.g., maids or drivers), we act as a connector.
                Employers are responsible for lawful contracts, wages, taxes, and safe working
                conditions. Candidates must present truthful qualifications. We do not guarantee
                visa/work‑permit eligibility.
              </p>
            </Section>

            <Section id="content" title="11. User Content & Reviews">
              <ul>
                <li>
                  You may submit reviews or feedback that is fair, lawful, and non‑defamatory.
                </li>
                <li>
                  You grant us a non‑exclusive, worldwide, royalty‑free license to display such
                  content on the Platform.
                </li>
                <li>We may remove content that violates these Terms or applicable law.</li>
              </ul>
            </Section>

            <Section id="prohibited" title="12. Prohibited Uses">
              <ul>
                <li>Fraud, misrepresentation, money‑laundering, or other unlawful activity.</li>
                <li>Harassment, discrimination, or violation of privacy or IP rights.</li>
                <li>Scraping, reverse engineering, or interfering with Platform security.</li>
              </ul>
            </Section>

            <Section id="ip" title="13. Intellectual Property">
              <p>
                The Platform and all materials, trademarks, and content are owned by {company} or
                its licensors and protected by IP laws. Limited, revocable, non‑transferable access
                is granted solely to use the Platform as intended.
              </p>
            </Section>

            <Section id="privacy" title="14. Privacy & Communications">
              <p>
                Our{" "}
                <Link component={RouterLink} to="/privacy-policy">
                  Privacy Policy
                </Link>{" "}
                explains how we collect and process personal data. By using the Platform, you
                consent to receiving transactional communications and, where permitted, marketing
                messages which you may opt out of.
              </p>
            </Section>

            <Section id="warranties" title="15. Disclaimers & Warranties">
              <p>
                The Platform is provided on an “as is” and “as available” basis. We disclaim all
                warranties, express or implied, including merchantability, fitness for a particular
                purpose, and non‑infringement. Information and availability are subject to change.
              </p>
            </Section>

            <Section id="liability" title="16. Limitation of Liability">
              <p>
                To the maximum extent permitted by law, {company} will not be liable for indirect,
                incidental, special, consequential, or punitive damages, or for lost profits,
                revenues, or data, arising from or related to your use of the Platform or
                third‑party services.
              </p>
            </Section>

            <Section id="indemnity" title="17. Indemnification">
              <p>
                You agree to indemnify and hold harmless {company} and its team against claims,
                damages, liabilities, and expenses arising from your misuse of the Platform or
                breach of these Terms.
              </p>
            </Section>

            <Section id="termination" title="18. Suspension & Termination">
              <p>
                We may suspend or terminate access for violations, suspected fraud, or operational
                risk. You may stop using the Platform at any time; certain obligations (e.g., fees
                owed) survive.
              </p>
            </Section>

            <Section id="law" title="19. Governing Law & Disputes">
              <p>
                These Terms are governed by the laws of Rwanda and, where relevant, applicable East
                African Community regulations. Disputes shall be resolved amicably; failing that,
                they may be submitted to competent courts in Kigali, Rwanda, unless another forum is
                mandated by law.
              </p>
            </Section>

            <Section id="changes" title="20. Changes to Terms">
              <p>
                We may update these Terms from time to time. Material changes will be signposted on
                the Platform. Continued use constitutes acceptance of the updated Terms.
              </p>
            </Section>

            <Section id="contact" title="21. Contact">
              <p>Questions? Email: support@thebesthelper.example or use our contact page.</p>
            </Section>

            <BackToTop />
          </Card>
        </Grid>
      </PageShell>
    </MKBox>
  );
}

function Section({ id, title, children }) {
  return (
    <MKBox id={id} mb={4}>
      <MKTypography variant="h5" fontWeight="bold" mb={1}>
        {title}
      </MKTypography>
      <Divider sx={{ mb: 2 }} />
      <MKTypography variant="body2" color="text" component="div" sx={{ lineHeight: 1.8 }}>
        {children}
      </MKTypography>
    </MKBox>
  );
}
